import { SEO } from '../components/SEO';
import { PageHero } from '../components/PageHero';
import { ScrollReveal } from '../components/ScrollReveal';
import { DarkTexture } from '../components/DarkTexture';
import { consentManagerContent } from '../data/consentManager';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const securityImg = 'https://images.unsplash.com/photo-1774898989484-0b9becf69efb?q=80&w=1200';

const ConsentManager = () => (
  <div className="bg-navy">
    <SEO
      title="DPDPA Consent Manager Explained — Rule 4 & First Schedule"
      description="How the DPDPA Consent Manager works: registration under Rule 4, the First Schedule obligations, and the interoperable consent dashboard for Data Principals."
      path="/consent-manager"
    />
    <PageHero
      testId="consent-manager-page-hero"
      kicker="Rule 4 · First Schedule"
      title="The Consent Manager"
      description="A registered, interoperable platform through which a Data Principal can give, manage, review and withdraw consent — one dashboard, every Data Fiduciary."
    />

    <section className="relative bg-navy py-16 sm:py-24 px-6 sm:px-10 lg:px-16 overflow-hidden" data-testid="consent-manager-content-section">
      <DarkTexture orbs={false} />
      <div className="relative max-w-5xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <ScrollReveal>
          <p className="text-white/60 text-base sm:text-lg leading-relaxed mb-8">{consentManagerContent.intro}</p>
          <div
            className="relative h-64 w-full overflow-hidden"
            style={{ clipPath: 'polygon(0 0, 100% 0, 100% 90%, 95% 100%, 0 100%)' }}
          >
            <img src={securityImg} alt="Data security" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-navy/30" />
          </div>
          <p className="font-mono text-xs text-cyan mt-4">{consentManagerContent.registrationWindow}</p>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <h2 className="font-serif text-2xl text-white mb-6">How it works</h2>
          <div className="space-y-6 mb-10">
            {consentManagerContent.howItWorks.map((h) => (
              <div key={h.title} className="border-l-2 border-cyan pl-5">
                <p className="text-white font-medium mb-1">{h.title}</p>
                <p className="text-sm text-white/50 leading-relaxed">{h.detail}</p>
              </div>
            ))}
          </div>
          <h2 className="font-serif text-2xl text-white mb-4">Ongoing obligations</h2>
          <ul className="space-y-3">
            {consentManagerContent.obligations.map((o, i) => (
              <li key={i} className="text-sm text-white/60 leading-relaxed flex gap-3">
                <span className="font-mono text-cyan">{String(i + 1).padStart(2, '0')}</span>{o}
              </li>
            ))}
          </ul>
        </ScrollReveal>
      </div>
    </section>

    <section className="bg-void py-20 px-6 sm:px-10 lg:px-16 text-center border-t border-white/5" data-testid="consent-manager-cta-section">
      <ScrollReveal>
        <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">Building consent architecture for your product?</h2>
        <Link to="/compliance-assessment" data-testid="consent-manager-cta-link" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan to-indiagreen text-navy px-6 py-3 font-semibold hover:shadow-[0_0_24px_rgba(0,240,255,0.45)] hover:scale-[1.03] transition-all duration-300">
          Get a Compliance Assessment <ArrowRight size={16} />
        </Link>
      </ScrollReveal>
    </section>
  </div>
);

export default ConsentManager;
